import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureToggle } from "shared/src/constants/featureToggleNames";
import ClaimableAuthenticatorV2 from "../../components/Shared/ClaimableAuthenticatorV2";
import ClaimableAuthenticator from "../../components/Shared/ClaimableAuthenticator";
import PageLoader from "../../components/Shared/PageLoader";
import styles from "./queryAuthGate.module.scss";

const FEATURE_FLAG_TIMEOUT_MS = 1000;

interface QueryAuthGateProps {
  children: React.ReactNode;
}

const QueryAuthGate: React.FC<QueryAuthGateProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const newUser =
    searchParams.get("newUser")?.toLowerCase() === "true" || false;
  const email = searchParams.get("email");
  const { user, authStatus } = useAuthenticator();
  const isAuthenticated = Boolean(user) && authStatus === "authenticated";
  const featureFlagValue = useFeatureFlagEnabled(FeatureToggle.AUTH_FLOW_V2);
  const [fallbackToV1, setFallbackToV1] = useState(false);

  const isLoading = featureFlagValue === undefined && !fallbackToV1;
  const isV2 = featureFlagValue === true && !fallbackToV1;

  useEffect(() => {
    if (featureFlagValue !== undefined) return;

    const timer = setTimeout(() => {
      setFallbackToV1(true);
    }, FEATURE_FLAG_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [featureFlagValue]);

  const defaultTab = newUser ? "signUp" : "signIn";

  if (isLoading) {
    return <PageLoader />;
  }

  if (isV2) {
    return (
      <QueryAuthGateV2 defaultTab={defaultTab} email={email}>
        {children}
      </QueryAuthGateV2>
    );
  }

  return (
    <QueryAuthGateV1
      isAuthenticated={isAuthenticated}
      defaultTab={defaultTab}
      email={email}
      newUser={newUser}
    >
      {children}
    </QueryAuthGateV1>
  );
};

interface QueryAuthGateV2Props {
  children: React.ReactNode;
  defaultTab: "signUp" | "signIn";
  email: string | null;
}

function QueryAuthGateV2({
  children,
  defaultTab,
  email,
}: QueryAuthGateV2Props) {
  return (
    <ClaimableAuthenticatorV2
      defaultTab={defaultTab}
      prefilledEmail={email || undefined}
    >
      {children}
    </ClaimableAuthenticatorV2>
  );
}

interface QueryAuthGateV1Props {
  children: React.ReactNode;
  isAuthenticated: boolean;
  defaultTab: "signUp" | "signIn";
  email: string | null;
  newUser: boolean;
}

function QueryAuthGateV1({
  children,
  isAuthenticated,
  defaultTab,
  email,
  newUser,
}: QueryAuthGateV1Props) {
  let formFields;

  if (newUser) {
    formFields = email
      ? {
          signUp: {
            email: {
              defaultValue: email,
              isRequired: true,
              isReadOnly: true,
              className: styles.authenticatorEmailField,
            },
          },
        }
      : undefined;
  } else if (!newUser) {
    formFields = email
      ? {
          signIn: {
            username: {
              defaultValue: email,
              isRequired: true,
              isReadOnly: true,
              className: styles.authenticatorEmailField,
            },
          },
        }
      : undefined;
  }

  if (isAuthenticated) {
    return (
      <>
        <ClaimableAuthenticator
          defaultTab={defaultTab}
          formFields={formFields}
        />
        {children}
      </>
    );
  }

  return (
    <ClaimableAuthenticator defaultTab={defaultTab} formFields={formFields} />
  );
}

export default QueryAuthGate;
